import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AlertTriangle, Check, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import pLimit from "p-limit";
import { toast } from "sonner";
import {
  apiDelete,
  apiGet,
  apiPost,
  queryClient,
  retryOnceOnNetwork,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { cn } from "@/lib/utils";
import type { FileUploadStatus, UploadedFile } from "./bulk-upload";

// Display metadata for the merged row status. Lives here rather than in
// bulk-upload.tsx because only this file renders status badges now.
const FILE_STATUS_LABELS: Record<FileUploadStatus, string> = {
  converting: "Converting",
  uploading: "Uploading",
  uploaded: "Uploaded",
  processing: "Processing",
  processed: "Ready to confirm",
  confirmed: "Confirmed",
  error: "Failed",
};

const FILE_STATUS_STYLES: Record<FileUploadStatus, string> = {
  converting: "bg-muted text-muted-foreground border-border",
  uploading: "bg-muted text-muted-foreground border-border",
  uploaded: "bg-muted text-muted-foreground border-border",
  processing: "bg-info-muted text-info border-info/20",
  processed: "bg-warning-muted text-warning border-warning/20",
  confirmed: "bg-success-muted text-success border-success/20",
  error: "bg-destructive/10 text-destructive border-destructive/20",
};

// HTTP concurrency cap for auto-resume + batch-discard. Not related to the
// backend's confirmed-receipt cap; purely a client-side throttle.
export const PROCESS_CONCURRENCY = 10;

interface TrayReceipt {
  id: string;
  file_path: string;
  // Original uploaded filename, preserved with spaces and user-visible
  // characters. Empty string for legacy rows uploaded before the field
  // existed — frontend falls through to vendor_name in that case.
  file_name: string;
  presigned_url: string;
  confirmed: boolean;
  has_raw_data: boolean;
  vendor_name: string;
  created: string;
}

function isPdfPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return /\.pdf(\?|$)/i.test(path);
}

interface TrayListResponse {
  results: TrayReceipt[];
  total_count: number;
}

interface MergedRow {
  key: string;
  receiptId?: string;
  name: string;
  status: FileUploadStatus;
  thumbnailUrl?: string;
  isPdf: boolean;
  // Populated only on error rows — the File the accountant originally
  // dropped, retained in state so they can click Retry upload without
  // re-picking from disk.
  retryFile?: File;
}

export function ReceiptTray({
  userId,
  uploadFiles,
  setUploadFiles,
  uploadFile,
  onUploadMore,
}: {
  userId: string;
  uploadFiles: UploadedFile[];
  setUploadFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  /** Shared uploader passed from the parent so the per-row Retry button
   *  goes through the exact same code path (tempId reuse, /upload → S3 PUT)
   *  as the drop zone. */
  uploadFile: (file: File, reuseTempId?: string) => Promise<void>;
  /** Optional "Upload more" handler. When provided, renders a top-right
   *  Upload more button that triggers the same file picker as the main one. */
  onUploadMore?: () => void;
}) {
  const navigate = useNavigate();
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["accountant-receipts-tray", userId],
    queryFn: () =>
      apiGet<TrayListResponse>(
        `/api/accountant/users/${userId}/receipts?status=tray&limit=100`,
      ),
    // Poll while anything is in-flight — cheap; stops once nothing is
    // processing on either side of the client/server boundary.
    refetchInterval: (q) => {
      const d = q.state.data as TrayListResponse | undefined;
      const serverProcessing = d?.results?.some((r) => !r.has_raw_data) ?? false;
      const clientInFlight = uploadFiles.some(
        (f) => f.status === "uploading" || f.status === "uploaded",
      );
      return serverProcessing || clientInFlight ? 2000 : false;
    },
  });

  // Stable reference so the useMemo / useEffect deps below don't re-fire
  // every render just because `data?.results ?? []` allocates a new array.
  const receipts = useMemo(() => data?.results ?? [], [data]);
  const processing = useMemo(
    () => receipts.filter((r) => !r.has_raw_data),
    [receipts],
  );

  // Merge: client in-flight rows joined with server receipts by receiptId.
  // Server rows the client doesn't know about are appended at the end
  // (orphans from a prior session, or client-cleared rows).
  //
  // Row label resolution: prefer the original filename so the accountant
  // keeps the visual anchor they had when they dropped the file. The
  // in-session row uses File.name (exact characters as dragged); after a
  // refresh or in cross-session orphan rows, fall through file_name →
  // vendor_name → "Receipt" so legacy rows (file_name='') still degrade
  // gracefully to the prior behaviour.
  const mergedRows = useMemo<MergedRow[]>(() => {
    const rows: MergedRow[] = [];
    const knownReceiptIds = new Set<string>();

    for (const f of uploadFiles) {
      const server = f.receiptId
        ? receipts.find((r) => r.id === f.receiptId)
        : undefined;
      if (f.receiptId) knownReceiptIds.add(f.receiptId);

      let status: FileUploadStatus = f.status;
      if (f.status === "uploaded") {
        // Upload is done; server drives the rest of the lifecycle.
        if (server?.confirmed) status = "confirmed";
        else if (server?.has_raw_data) status = "processed";
        else status = "processing";
      }

      const fallbackIsPdf = server
        ? isPdfPath(server.file_path)
        : f.name.toLowerCase().endsWith(".pdf");

      rows.push({
        key: f.id,
        receiptId: f.receiptId,
        name: f.name,
        status,
        thumbnailUrl: f.previewUrl ?? server?.presigned_url,
        isPdf: !f.previewUrl && fallbackIsPdf,
        retryFile: f.status === "error" ? f.file : undefined,
      });
    }

    for (const r of receipts) {
      if (knownReceiptIds.has(r.id)) continue;
      const status: FileUploadStatus = r.confirmed
        ? "confirmed"
        : r.has_raw_data
          ? "processed"
          : "processing";
      rows.push({
        key: r.id,
        receiptId: r.id,
        name: r.file_name || r.vendor_name || "Receipt",
        status,
        thumbnailUrl: r.presigned_url,
        isPdf: isPdfPath(r.file_path),
      });
    }

    return rows;
  }, [uploadFiles, receipts]);

  const uploadingCount = mergedRows.filter(
    (r) => r.status === "uploading",
  ).length;
  const processingCount = mergedRows.filter(
    (r) => r.status === "processing",
  ).length;
  const readyCount = mergedRows.filter((r) => r.status === "processed").length;
  const errorCount = mergedRows.filter((r) => r.status === "error").length;

  // Auto-resume: fire /process for any uploaded-but-not-yet-processed receipts.
  // Skips receipts whose client row is still "uploading" (S3 PUT not yet done
  // — the corresponding S3 file isn't there for OpenAI to fetch). p-limit
  // caps effective concurrency.
  const limiterRef = useRef<ReturnType<typeof pLimit> | null>(null);
  const firedRef = useRef<Set<string>>(new Set());
  // inFlightRef prevents concurrent /process calls for the same receipt id.
  // firedRef prevents re-firing after a completed attempt.
  // failedProcessingIds (state, so the Retry processing button can react
  // when its visibility changes) tracks attempts that errored — those are
  // the only ones the button retries. Items legitimately mid-OCR (in
  // inFlightRef) or that completed successfully are left alone so retry
  // doesn't kick off a duplicate OCR run.
  const inFlightRef = useRef(new Set<string>());
  const [failedProcessingIds, setFailedProcessingIds] = useState<Set<string>>(
    new Set(),
  );
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    if (!limiterRef.current) limiterRef.current = pLimit(PROCESS_CONCURRENCY);
    const limiter = limiterRef.current;
    const pending = processing.filter((r) => {
      if (firedRef.current.has(r.id)) return false;
      if (inFlightRef.current.has(r.id)) return false;
      const clientEntry = uploadFiles.find((f) => f.receiptId === r.id);
      // Skip if bulk-upload is still doing S3 PUT (no file to OCR yet) or
      // the upload failed (no file to OCR, ever, without a re-drop).
      if (clientEntry?.status === "uploading") return false;
      if (clientEntry?.status === "error") return false;
      return true;
    });
    if (!pending.length) return;
    for (const r of pending) {
      firedRef.current.add(r.id);
      inFlightRef.current.add(r.id);
      void limiter(() =>
        retryOnceOnNetwork(() =>
          apiPost(
            `/api/accountant/users/${userId}/receipts/${r.id}/process`,
          ),
        )
          .then(() => {
            // Success clears any prior failure marker (e.g. a retry that
            // succeeded on the second attempt).
            setFailedProcessingIds((prev) => {
              if (!prev.has(r.id)) return prev;
              const next = new Set(prev);
              next.delete(r.id);
              return next;
            });
          })
          .catch(() => {
            // Record the failure so Retry processing knows which ids are
            // safe to re-fire. firedRef stays set so auto-resume doesn't
            // spin on a persistently failing OCR — only an explicit retry
            // click will clear it.
            setFailedProcessingIds((prev) => {
              if (prev.has(r.id)) return prev;
              const next = new Set(prev);
              next.add(r.id);
              return next;
            });
          })
          .finally(() => {
            inFlightRef.current.delete(r.id);
            queryClient.invalidateQueries({
              queryKey: ["accountant-receipts-tray", userId],
            });
            queryClient.invalidateQueries({
              queryKey: ["accountant-receipts", userId],
            });
          }),
      );
    }
  }, [processing, uploadFiles, userId, retryKey]);

  const batchConfirm = useMutation({
    mutationFn: (ids: string[]) =>
      apiPost<{
        confirmed_ids: string[];
        rejected_ids: string[];
        detail: string;
      }>(`/api/accountant/users/${userId}/receipts/batch-confirm`, {
        receipt_ids: ids,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts-tray", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
      // Drop confirmed rows from the client in-flight list.
      if (res.confirmed_ids.length) {
        const confirmed = new Set(res.confirmed_ids);
        setUploadFiles((prev) =>
          prev.filter((f) => !f.receiptId || !confirmed.has(f.receiptId)),
        );
      }
      if (res.rejected_ids.length) {
        toast.warning(res.detail || "Some receipts could not be confirmed");
      } else if (res.confirmed_ids.length) {
        toast.success(`Confirmed ${res.confirmed_ids.length} receipts`);
      }
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not confirm"),
  });

  const discardAll = useMutation({
    mutationFn: async () => {
      if (!limiterRef.current) limiterRef.current = pLimit(PROCESS_CONCURRENCY);
      const limiter = limiterRef.current;
      await Promise.all(
        receipts.map((r) =>
          limiter(() =>
            apiDelete(`/api/accountant/users/${userId}/receipts/${r.id}`),
          ),
        ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts-tray", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
      // Clear ALL client rows including error rows. Error rows already have
      // their server record DELETEd by the orphan-cleanup path, so there's
      // nothing left on the server; keeping them client-side confused the
      // accountant who expected Discard-all to wipe everything in view.
      // Retrying an errored upload is still possible via the per-row Retry
      // upload button (before Discard) or by re-dropping the file.
      setUploadFiles([]);
      toast.success("Batch discarded");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not discard"),
  });

  const headline = useMemo(() => {
    if (!mergedRows.length) return "";
    const n = mergedRows.length;
    const noun = n === 1 ? "receipt" : "receipts";
    if (uploadingCount && !readyCount && !processingCount) {
      return `Uploading ${uploadingCount} ${noun}...`;
    }
    if (processingCount && !readyCount) {
      return `Processing ${n} ${noun}...`;
    }
    return `${n} ${noun} awaiting your review`;
  }, [mergedRows.length, uploadingCount, processingCount, readyCount]);

  if (!mergedRows.length) return null;

  const clientInFlight = uploadingCount;
  const serverInFlight = processingCount;
  const anyReady = readyCount > 0;
  const canReview = anyReady && serverInFlight === 0 && clientInFlight === 0;
  const waitingHint =
    clientInFlight + serverInFlight > 0
      ? `Waiting for ${clientInFlight + serverInFlight} more to finish`
      : undefined;

  // Only confirmable ready rows; exclude any uploading/processing/errored rows.
  const confirmableReceiptIds = mergedRows
    .filter((r) => r.status === "processed" && r.receiptId)
    .map((r) => r.receiptId!);

  return (
    <div
      role="region"
      aria-live="polite"
      className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{headline}</p>
          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
            {uploadingCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Loader2
                  className="size-3 animate-spin text-muted-foreground"
                  aria-hidden
                />
                {uploadingCount} uploading
              </span>
            )}
            {processingCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Loader2
                  className="size-3 animate-spin text-info"
                  aria-hidden
                />
                {processingCount} processing
              </span>
            )}
            {readyCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Check className="size-3 text-success" aria-hidden />
                {readyCount} ready
              </span>
            )}
            {errorCount > 0 && (
              <span className="inline-flex items-center gap-1 text-destructive">
                <AlertTriangle className="size-3" aria-hidden />
                {errorCount} failed
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {onUploadMore && (
            <Button
              size="sm"
              variant="outline"
              onClick={onUploadMore}
              title="Upload more receipts"
            >
              <Plus className="mr-1.5 size-3" aria-hidden />
              Upload more
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => navigate(`/accountant/clients/${userId}/review`)}
            disabled={!canReview}
            title={waitingHint ?? "Review receipts one by one"}
          >
            Review
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => batchConfirm.mutate(confirmableReceiptIds)}
            disabled={!canReview || batchConfirm.isPending}
            title={waitingHint ?? "Confirm all ready receipts"}
          >
            {batchConfirm.isPending ? (
              <Loader2 className="mr-1.5 size-3 animate-spin" />
            ) : null}
            Confirm all
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmDiscardOpen(true)}
            disabled={discardAll.isPending}
            aria-label="Discard all"
          >
            <Trash2 className="size-3.5" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Merged per-file list: client in-flight rows + server pending/ready.
          Rows become clickable (jump straight to the review page for that
          receipt) only when ALL items have finished processing. Mixing
          clickable + not-clickable rows while processing is still in flight
          is confusing, so the clickability gate waits for canReview.
          Rendered as <div> (not <button>) so the error-row Retry button
          can nest inside without invalid HTML. */}
      <div className="rounded-lg border divide-y bg-background">
        {mergedRows.map((row) => {
          const isClickable =
            canReview && row.status === "processed" && !!row.receiptId;
          const onRowClick = isClickable
            ? () =>
                navigate(
                  `/accountant/clients/${userId}/review?at=${row.receiptId}`,
                )
            : undefined;
          return (
            <div
              key={row.key}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={onRowClick}
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick?.();
                      }
                    }
                  : undefined
              }
              className={cn(
                "w-full text-left flex items-center gap-3 px-3 py-2.5",
                isClickable &&
                  "hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 cursor-pointer",
              )}
              title={
                row.status === "error"
                  ? "Upload failed. Click Retry to upload again."
                  : isClickable
                    ? "Open this receipt in review"
                    : undefined
              }
            >
              <div className="size-10 rounded bg-muted shrink-0 overflow-hidden flex items-center justify-center">
                {row.isPdf ? (
                  <span className="text-[10px] text-muted-foreground font-medium">
                    PDF
                  </span>
                ) : row.thumbnailUrl ? (
                  <img
                    src={row.thumbnailUrl}
                    alt={row.name}
                    className="w-full h-full object-cover"
                    onError={(e) =>
                      ((e.target as HTMLImageElement).style.display = "none")
                    }
                  />
                ) : null}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{row.name}</p>
              </div>

              <div className="shrink-0 flex items-center gap-2">
                {(row.status === "converting" ||
                  row.status === "uploading" ||
                  row.status === "processing") && (
                  <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                )}
                {row.status === "processed" && (
                  <Check className="size-3.5 text-warning" />
                )}
                {row.status === "confirmed" && (
                  <Check className="size-3.5 text-success" />
                )}
                <Badge
                  variant="outline"
                  className={cn("text-[10px]", FILE_STATUS_STYLES[row.status])}
                >
                  {FILE_STATUS_LABELS[row.status]}
                </Badge>
                {row.status === "error" && row.retryFile && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      void uploadFile(row.retryFile!, row.key);
                    }}
                  >
                    <RefreshCw className="mr-1 size-3" aria-hidden />
                    Retry upload
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {batchConfirm.data?.rejected_ids.length ? (
        <div className="flex items-start gap-2 text-xs text-warning">
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5" aria-hidden />
          <span>
            {batchConfirm.data.rejected_ids.length} receipt(s) couldn't be
            confirmed. The client is at the free plan limit.
          </span>
        </div>
      ) : null}

      {failedProcessingIds.size > 0 && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            // Only retry items we KNOW failed. Items still in-flight
            // (inFlightRef) or that completed successfully stay out of
            // failedProcessingIds, so this won't kick off a duplicate OCR
            // run for something that's actively being processed. The
            // failures graduate back into the auto-resume candidate pool
            // by being removed from firedRef.
            for (const id of failedProcessingIds) {
              firedRef.current.delete(id);
            }
            setFailedProcessingIds(new Set());
            setRetryKey((k) => k + 1);
            queryClient.invalidateQueries({
              queryKey: ["accountant-receipts-tray", userId],
            });
          }}
        >
          <RefreshCw className="mr-1.5 size-3" aria-hidden />
          Retry processing ({failedProcessingIds.size})
        </Button>
      )}

      <ConfirmDialog
        open={confirmDiscardOpen}
        onOpenChange={setConfirmDiscardOpen}
        title={`Discard all ${mergedRows.length} receipts?`}
        description="This can't be undone. Any unconfirmed uploads for this client will be removed."
        confirmLabel="Discard all"
        destructive
        loading={discardAll.isPending}
        onConfirm={() =>
          discardAll.mutate(undefined, {
            onSuccess: () => setConfirmDiscardOpen(false),
          })
        }
      />
    </div>
  );
}
